<template>
  <v-layout column>
    <v-toolbar height="56" v-if="!planningLoad">
      <v-toolbar-title>
        {{ bannerTitle }}
      </v-toolbar-title>
    </v-toolbar>
    <v-toolbar height="56" v-if="planningLoad"
               :class="{'mobile-toolbar': isMobile}" >
      <v-toolbar-title>
        {{ planningLoad.humanId }},
        {{ displayLoadCount | pluralize('truck', { includeNumber: true }) }},
        {{ distanceLabel(planningLoad.loadGroup.details.loadedMiles) }}
      </v-toolbar-title>

      <v-layout :slot="isMobile ? 'extension' : null">
        <v-spacer v-if="!isMobile"></v-spacer>
        <v-toolbar-items>
          <v-btn small flat color="primary"
                 v-if="!isMobile"
                 :loading="loading"
                 @click="$emit('reload')">
            <v-layout column>
              <v-icon>refresh</v-icon>
              Reload
            </v-layout>
          </v-btn>
          <v-tabs v-model="tab"
                  :right="!isMobile"
                  :centered="isMobile"
                  grow show-arrows
                  color="#f5f5f5"
                  slider-color="primary">
            <v-tab key="Details" to="#details">
              <v-layout column class="align-center">
                <v-icon>list</v-icon>
                Details
              </v-layout>
            </v-tab>
            <v-tab key="Customer"
                   to="#customer"
                   v-if="showCustomerTab">
              <v-layout column class="align-center">
                <v-icon>business</v-icon>
                Customer
              </v-layout>
            </v-tab>
            <v-tab key="Booked"
                   to="#booked"
                   v-if="showBookedLoads">
              <v-layout column class="align-center">
                <v-icon>event</v-icon>
                <v-progress-linear class="mt-2 mb-0"
                                   v-if="loadingBookedLoads"
                                   :indeterminate="true">
                </v-progress-linear>
                <template v-else>
                  Booked ({{ bookedLoadsCount }})
                </template>
              </v-layout>
            </v-tab>
            <v-tab key="Dispatch"
                   to="#dispatch"
                   v-if="planning">
              <v-layout column class="align-center">
                <v-icon>share</v-icon>
                Network
              </v-layout>
            </v-tab>
            <v-tab key="LiveLoadboard"
                   to="#live_loadboard"
                   v-if="planning && isLoadboardEnabled">
              <v-layout column class="align-center">
                <v-icon>gavel</v-icon>
                Live Bids
              </v-layout>
            </v-tab>
            <v-tab key="ScheduleRepeatLoads"
                   to="#schedule"
                   v-if="repeating">
              <v-layout column class="align-center">
                <v-icon>sync</v-icon>
                Schedule
              </v-layout>
            </v-tab>
            <v-tab key="Map"
                   to="#map">
              <v-layout column class="align-center">
                <v-icon>map</v-icon>
                Map
              </v-layout>
            </v-tab>
          </v-tabs>
        </v-toolbar-items>
      </v-layout>
    </v-toolbar>

    <div class="pt-1"
         :class="{'scroll-section': !isMobile}">
      <v-card flat>
        <v-tabs-items v-model="tab">
          <v-tab-item value="details"
                      :transition="false"
                      :reverse-transition="false">
            <v-edit-load-details v-if="planningLoad"
                                 :company="company"
                                 :disableAddressView="disableAddressView"
                                 :dispatched="dispatched"
                                 :repeating="repeating"
                                 :read-only="readOnly"
                                 @onFormChange="$emit('onFormChange',$event)"
                                 @refresh="$emit('updateLoad',$event)"
                                 @savingLoad="$emit('savingLoad', $event)"
                                 :planning-load="planningLoad">
              <v-toolbar-items slot="toolbarItems">
                <slot name="toolbarButtons"></slot>
              </v-toolbar-items>
            </v-edit-load-details>
          </v-tab-item>
          <v-tab-item value="customer"
                      :transition="false"
                      :reverse-transition="false"
                      v-if="showCustomerTab">
            <company-settings
              :prop-company-id="planningLoad.loadGroup.details.customerCompany.id">
            </company-settings>
          </v-tab-item>
          <v-tab-item v-if="showBookedLoads"
                      value="booked"
                      :transition="false"
                      :reverse-transition="false">
            <v-booked v-if="company && planningLoad"
                      :planningLoad="planningLoad"
                      :covered-loads="groupBookedLoads"
                      :company="company"
                      :loading="loading"
                      :class="{'pl-2': !isMobile}"
                      @reload="$emit('cancelLoad', $event)"
                      @assignMyTrucks="$confirm('assign the trucks already')"
                      @refreshLoad="$emit('reload', $event)">
            </v-booked>
          </v-tab-item>
          <v-tab-item v-if="planning"
                      lazy
                      value="dispatch"
                      :transition="false"
                      :reverse-transition="false">
            <slot name="dispatchTab">

            </slot>
          </v-tab-item>
          <v-tab-item v-if="isLoadboardEnabled && planning"
                      lazy
                      value="live_loadboard"
                      :transition="false"
                      :reverse-transition="false">
            <slot name="liveLoadBoardTab">

            </slot>
          </v-tab-item>
          <v-tab-item v-if="repeating" lazy
                      value="schedule"
                      :transition="false" :reverse-transition="false">
            <v-schedule-repeat-loads  v-if="planningLoad"
                                      @refresh="$emit('updateLoad',$event)"
                                      @dirty="$emit('onFormChange',$event)"
                                      :load="planningLoad" />
          </v-tab-item>
          <v-tab-item value="map" lazy
                      :transition="false" :reverse-transition="false">
            <slot name="loadMap">
              <load-map class="py-1 px-1"
                        :disableZoom="disableAddressView"
                        hideOnSmall
                        v-if="planningLoad"
                        :origin-place-id="_.get(planningLoad, 'loadGroup.details.pickupLocation.googlePlace.placeId', null)"
                        :destination-place-id="_.get(planningLoad, 'loadGroup.details.dropoffLocation.googlePlace.placeId', null)">
              </load-map>
            </slot>
          </v-tab-item>

        </v-tabs-items>
      </v-card>
    </div>
  </v-layout>
</template>

<script>
import _ from 'lodash';
import Vue2Filters from 'vue2-filters';
import loadGroupMixin from '@/mixins/loadGroupMixin';
import bookedLoadMixin from '@/mixins/bookedLoadMixin';

import LoadMap from '@/components/LoadMap';
import VEditLoadDetails from '@/components/InfoWidgets/EditLoadDetails';
import VBooked from '@/pages/Companies/Loads/components/Booked';
import CompanySettings from '@/pages/Companies/Settings';
import VScheduleRepeatLoads from '@/components/InfoWidgets/ScheduleRepeatLoads';
import pluginMixin from '../../../mixins/pluginMixin';
import unitsOfMeasurePlugin from '../../../mixins/unitsOfMeasurePlugin';

export default {
  name: 'VLoadGroupDetails',
  mixins: [
    bookedLoadMixin,
    loadGroupMixin,
    Vue2Filters.mixin,
    pluginMixin,
    unitsOfMeasurePlugin,
  ],
  components: {
    VScheduleRepeatLoads,
    VBooked,
    LoadMap,
    VEditLoadDetails,
    CompanySettings,
  },
  props: {
    planningLoad: {
      type: Object,
    },
    disableAddressView: {
      type: Boolean,
      default: false,
    },
    selectedLoad: {
      type: Object,
      default: null,
    },
    company: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    bannerTitle: {
      type: String,
      required: true,
    },
    groupBookedLoads: {
      type: Array,
      default: null,
    },
    dispatched: {
      type: Boolean,
      default: false,
    },
    repeating: {
      type: Boolean,
      default: false,
    },
    planning: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    tab: null,
  }),
  computed: {
    _() {
      return _;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    bookedLoadsCount() {
      const loads = this.groupBookedLoads || this.planningLoad.bookedLoads;
      return this.countBookedLoads(loads);
    },
    showBookedLoads() {
      return this.planning || this.groupBookedLoads != null;
    },
    showCustomerTab() {
      return (this.repeating || this.dispatched || this.planning) && _.get(this.planningLoad, 'loadGroup.details.customerCompany.id', false);
    },
    displayLoadCount() {
      const lgAvailableCount = this.planningLoad.loadGroup.availableLoadCount;
      if (this.repeating) {
        // TODO: add up all the week's trucks
        return this.planningLoad.loadGroup.loadCount;
      }
      if (this.dispatched) {
        const visibleCount = this.planningLoad.visibleCount;
        return _.min([visibleCount, lgAvailableCount]);
      }

      return lgAvailableCount;
    },
  },
  methods: {
    countBookedLoads(loads) {
      return _.filter(loads, l => !this.isBookedLoadCanceled(l)).length;
    },
    isBookedLoadCanceled(booked) {
      return booked.loadStatus === 'CANCELED';
    },
  },
  watch: {
    planningLoad(newVal) {
      if (!this.$route.hash) {
        // TODO: there's a one-off here. ugh.
        // this.tab = 0;
      }
    },
  },
};
</script>

<style>
  .mobile-toolbar .v-toolbar__extension {
    padding-left: 0;
    padding-right: 0;
  }
</style>

<style scoped>
  .scroll-section {
    height: calc(100vh - 235px);
    overflow: auto;
  }
</style>




// WEBPACK FOOTER //
// src/components/dashboard/simple/LoadGroupDetails.vue